import {useRef, useState, useEffect} from "react";
import "./CustomScroll.css";
import {ReactComponent as ScrollTrackLightIcon} from "../../../assets/svg/cover-scroll-track-light-original.svg";
import {ReactComponent as ScrollTrackDarkIcon} from "../../../assets/svg/cover-scroll-track-dark-original.svg";


const MIN_THUMB_WIDTH = 24;

export default function CustomScroll({children}) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [thumb, setThumb] = useState({width: 0, left: 0});
  const dragState = useRef({isDragging: false, moved: false, startX: 0, startScrollLeft: 0});


  const updateProgress = () => {
    const el = containerRef.current;
    const track = trackRef.current;
    if (!el || !track) return;

    const trackWidth = track.clientWidth;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbWidth = Math.min(
      trackWidth,
      Math.max(MIN_THUMB_WIDTH, (el.clientWidth / el.scrollWidth) * trackWidth)
    );
    const maxThumbOffset = trackWidth - thumbWidth;
    const left = maxScroll > 0 ? (el.scrollLeft / maxScroll) * maxThumbOffset : 0;

    setThumb({width: thumbWidth, left});
  };


  const scrollLeft = () => {
    containerRef.current.scrollBy({
      left: -200,
      behavior: "smooth"
    });
  };


  const scrollRight = () => {
    containerRef.current.scrollBy({
      left: 200,
      behavior: "smooth"
    });
  };


  const handlePointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const el = containerRef.current;
    dragState.current = {
      isDragging: true,
      moved: false,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft
    };
    el.setPointerCapture(e.pointerId);
  };


  const handlePointerMove = (e) => {
    if (!dragState.current.isDragging) return;
    const el = containerRef.current;
    const delta = e.clientX - dragState.current.startX;
    if (Math.abs(delta) > 3) dragState.current.moved = true;
    el.scrollLeft = dragState.current.startScrollLeft - delta;
  };


  const endDrag = (e) => {
    if (!dragState.current.isDragging) return;
    dragState.current.isDragging = false;
    const el = containerRef.current;
    if (e && el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
  };


  const handleClickCapture = (e) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  };


  useEffect(() => {
    const el = containerRef.current;
    const track = trackRef.current;

    el.addEventListener("scroll", updateProgress);
    window.addEventListener("resize", updateProgress);

    const ro = new ResizeObserver(updateProgress);
    ro.observe(el);
    if (track) ro.observe(track);

    updateProgress();

    return () => {
      el.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
      ro.disconnect();
    };
  }, []);


  return (
    <div className="scroll-root">
      <div
        className="scroll-container"
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleClickCapture}
      >
        {children}
      </div>

      <div className="scroll-wrapper">
        <button className="scroll-btn left" onClick={scrollLeft}>«</button>

        <div className="scroll-track" ref={trackRef}>
          <ScrollTrackLightIcon
            className="scroll-track-bg scroll-track-bg-light"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          />
          <ScrollTrackDarkIcon
            className="scroll-track-bg scroll-track-bg-dark"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          />

          <div
            className="scroll-progress"
            style={{
              width: thumb.width + "px",
              transform: `translateX(${thumb.left}px)`
            }}
          ></div>
        </div>

        <button className="scroll-btn right" onClick={scrollRight}>»</button>
      </div>
    </div>
  );
}
